import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { fetchSingleWorkflow } from '../shared/redtailQueryHelpers'
import { applyTaskToggle, applyStepComplete } from './workflowOptimistic'

function workflowDetailKey (workflowId) {
  return [QUERY_KEYS.redtailDomainQuery, 'workflow-detail', workflowId]
}

export { workflowDetailKey }

export default function useWorkflowDetail (workflowId) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: workflowDetailKey(workflowId),
    queryFn: () => fetchSingleWorkflow(workflowId, clientId),
    staleTime: 30_000,
    enabled: !!workflowId
  })

  const workflow = query.data ?? null

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: workflowDetailKey(workflowId)
    })
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
    })
  }, [queryClient, workflowId])

  const toggleTask = useCallback(async (stepId, taskId, wasComplete) => {
    const command = wasComplete ? 'uncompleteWorkflowTask' : 'completeWorkflowTask'
    await postNamedCommand('coreData', 'execute-redtail-domain-command', {
      command,
      autoResolveConnection: true,
      clientId,
      request: { workflowId, stepId, taskId }
    })

    queryClient.setQueryData(
      workflowDetailKey(workflowId),
      (old) => applyTaskToggle(old, stepId, taskId, wasComplete)
    )

    invalidate()
  }, [clientId, workflowId, queryClient, invalidate])

  const completeStep = useCallback(async (stepId, outcomeId, notes, nextStepId) => {
    const request = { workflowId, stepId }
    if (outcomeId) request.outcome_id = outcomeId
    if (notes) request.step_notes = notes

    await postNamedCommand('coreData', 'execute-redtail-domain-command', {
      command: 'completeWorkflowStep',
      autoResolveConnection: true,
      clientId,
      request
    })

    queryClient.setQueryData(
      workflowDetailKey(workflowId),
      (old) => applyStepComplete(old, stepId, nextStepId)
    )

    invalidate()
  }, [clientId, workflowId, queryClient, invalidate])

  return {
    workflow,
    isLoading: query.isLoading,
    toggleTask,
    completeStep
  }
}
